/**
 * COMPASS LAB — chapter 3, where the two norths are places, not a definition.
 *
 * FOUNDER: "Compass: true north, magnetic north, variation, direction/azimuth."
 *
 * ── THE ONE THING THIS TEACHES ────────────────────────────────────────────────────────────────
 * Variation is a property of WHERE YOU ARE. A table states that; dragging a vessel across the
 * Atlantic and watching the two north lines open and close proves it, because the two poles are
 * visibly in different places and the angle between them at the ship is obviously a consequence of
 * her position. A cadet who has done that will never again think variation is a property of the
 * compass.
 *
 * ── AND THE THING IT REFUSES TO DO ────────────────────────────────────────────────────────────
 * It does NOT tell him the variation.
 *
 * Real variation comes from the full geomagnetic field — WMM or IGRF — and is read from the chart or
 * a model. The angle between "bearing to the geographic pole" and "bearing to the north magnetic
 * pole" is a geometric consequence of two points and is NOT that number: the field is not a dipole,
 * it is not symmetric, and in places the two disagree substantially.
 *
 * Showing that angle labelled "variation" would put a wrong figure in front of a candidate who is
 * examined on the right one, dressed in the authority of a diagram he just operated. So the angle is
 * shown as what it is — the geometric difference — and the value is refused in the engine's own
 * vocabulary, with the place to get it named. This is the compute/quote/null discipline arriving at
 * a teaching surface: the geometry is COMPUTED, the value is REFUSED, and nothing is invented in
 * between.
 */

import { useState } from 'react';
import NavSphere, { bearing, dm, NMP } from './navsphere.jsx';
import { Value } from '../../../packages/ankr-glass/src/react.jsx';
import { computed, refused, cited } from '../../../packages/ankr-provenance/src/provenance.mjs';

/* Positions where the geometry is strikingly different, so the point lands in three drags. */
const SPOTS = [
  { lat: 51.5, lon: -9.5, label: 'off Fastnet' },
  { lat: 45, lon: -50, label: 'the Grand Banks' },
  { lat: 70, lon: -60, label: 'Davis Strait' },
  { lat: 5, lon: 80, label: 'off Sri Lanka' },
];

export default function CompassLab() {
  const [pos, setPos] = useState({ lat: 51.5, lon: -9.5 });
  const [show, setShow] = useState({ poles: true, graticule: true, equator: true, primeMeridian: false });

  const tb = bearing(pos.lat, pos.lon, 90, 0);                 // toward the geographic pole
  const mb = bearing(pos.lat, pos.lon, NMP.lat, NMP.lon);      // toward the magnetic pole
  const diff = ((mb - tb + 540) % 360) - 180;                  // signed, west negative

  return (
    <div className="g-glass shell-panel lab">
      <div className="g-label">Compass lab · two norths</div>
      <h2 className="g-h2 lab-h">Why the compass does not point north</h2>
      <p className="g-body">
        Two poles sit in different places on the Earth:
        <b className="lab-key lab-true"> true north</b>, where the axis comes out, and
        <b className="lab-key lab-mag"> magnetic north</b>, which is somewhere else entirely and
        moves. Drag the vessel and watch the two lines from her open and close.
      </p>

      <div className="lab-globe">
        <NavSphere height={320} overlays={show}
          marks={[{ lat: pos.lat, lon: pos.lon, label: 'vessel', arcs: false }]}
          onMove={(lat, lon) => setPos({ lat, lon })} />
      </div>

      <div className="lab-readout">
        <div><span className="g-label">Position</span>
             <span className="lab-num">{dm(pos.lat, 'lat')} {dm(pos.lon, 'lon')}</span></div>
        <div><span className="g-label">To true north</span>
             <span className="lab-num lab-true">{tb.toFixed(1)}°</span></div>
        <div><span className="g-label">To magnetic north</span>
             <span className="lab-num lab-mag">{mb.toFixed(1)}°</span></div>
      </div>

      <div className="shell-picker lab-toggles">
        {SPOTS.map((s) => (
          <button key={s.label} className="g-btn" onClick={() => setPos({ lat: s.lat, lon: s.lon })}>
            {s.label}
          </button>
        ))}
      </div>

      <div className="lab-task">
        <div className="g-label">The geometry</div>
        <div className="lab-result">
          <Value of={computed(
            `${Math.abs(diff).toFixed(1)}° ${diff < 0 ? 'west' : 'east'} — the angle at the vessel ` +
            `between the direction to the geographic pole and the direction to the north magnetic ` +
            `pole. Move her and it changes, because it is a property of WHERE SHE IS.`)} />
        </div>

        {/* The refusal is the most important element on this surface. */}
        <div className="lab-result">
          <Value of={refused(
            'This is NOT the variation. Variation comes from the whole geomagnetic field, which is ' +
            'not a simple dipole — the World Magnetic Model gives it, and your chart prints it. The ' +
            'angle above shows only WHY variation exists and why it changes with position. Never ' +
            'write this number on a chart.')} />
        </div>

        <p className="g-body lab-why">
          The magnetic pole used here is <b>{NMP.lat}° N, {NMP.lon}° E ({NMP.epoch})</b>. It drifts
          tens of kilometres a year, which is itself the reason a chart carries the year of its
          variation and an annual rate of change.
        </p>
      </div>

      <div className="lab-def">
        <Value of={cited(
          'The variation is the angle between the magnetic and the geographic meridians at any place, '
          + 'and is named east or west according as the north point of the compass is drawn to the '
          + 'eastward or westward of the true north.',
          'Bowditch, The American Practical Navigator (1888) — public domain')} />
        <p className="g-body lab-why">
          &ldquo;At any place&rdquo; is the phrase the globe just demonstrated. Deviation — the
          ship&rsquo;s own iron, which changes with her HEAD rather than her position — is a separate
          error and is not shown here, because it does not live on the Earth.
        </p>
      </div>
    </div>
  );
}
